import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import AttendanceCell from "./AttendanceCell";
import AttendanceLegend from "./AttendanceLegend";
import {
	Card,
	DatePicker,
	Select,
	Space,
	Statistic,
	Typography,
	message,
} from "antd";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { antdKoKRSunday as locale } from "../../utilities/weekLocale";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

dayjs.locale("ko");

const { Text } = Typography;

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const buildCalendarWeeks = (month) => {
	const start = month.startOf("month");
	const end = month.endOf("month");
	const gridStart = start.subtract(start.day(), "day");
	const gridEnd = end.add(6 - end.day(), "day");
	const weeks = [];
	let cursor = gridStart;
	let week = [];
	while (!cursor.isAfter(gridEnd, "day")) {
		week.push(cursor);
		if (week.length === 7) {
			weeks.push(week);
			week = [];
		}
		cursor = cursor.add(1, "day");
	}
	return weeks;
};

const UserAttendance = () => {
	const isMobile = useIsMobile();
	const [month, setMonth] = useState(dayjs());
	const [employees, setEmployees] = useState([]);
	const [userId, setUserId] = useState(null);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);

	const loadEmployees = useCallback(async () => {
		try {
			const res = await useAxios.get("/users/employee-info/");
			const list = res.data || [];
			setEmployees(list);
			setUserId((prev) => prev || list[0]?.user_id || null);
		} catch {
			message.error("교직원 목록을 불러오지 못했습니다.");
		}
	}, []);

	const loadStatus = useCallback(async () => {
		if (!userId) {
			setData(null);
			return;
		}
		try {
			setLoading(true);
			const res = await useAxios.get("/attendance/status/", {
				params: {
					view: "user",
					user_id: userId,
					year: month.year(),
					month: month.month() + 1,
				},
			});
			setData(res.data);
		} catch {
			message.error("사용자별 출근 현황을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, [userId, month]);

	useEffect(() => {
		loadEmployees();
	}, [loadEmployees]);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	const employee = data?.employees?.[0];
	const dates = data?.dates || [];
	const summary = employee?.summary || data?.summary || {};
	const calendarWeeks = useMemo(() => buildCalendarWeeks(month), [month]);
	const dateByKey = useMemo(() => {
		const map = {};
		dates.forEach((item) => {
			map[item.date] = item;
		});
		return map;
	}, [dates]);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="사용자별 출근 현황" />
			<div className="page">
				<PageHeader
					title="사용자별 출근 현황"
				/>
				<Card className="surface-card" loading={loading}>
					<div className="attendance-toolbar">
						<Space wrap>
							<Select
								showSearch
								optionFilterProp="label"
								style={{
									minWidth: isMobile ? 0 : 220,
									width: isMobile ? "100%" : undefined,
								}}
								placeholder="직원 선택"
								value={userId}
								onChange={setUserId}
								options={employees.map((item) => ({
									value: item.user_id,
									label: `${item.user_name} (${item.attendance_code})`,
								}))}
							/>
							<DatePicker
								inputReadOnly={isMobile}
								picker="month"
								value={month}
								allowClear={false}
								onChange={(value) => value && setMonth(value)}
							/>
						</Space>
						<AttendanceLegend />
				</div>

				{employee ? (
					<>
						<Space size="large" wrap className="attendance-summary">
							<Statistic title="출근" value={summary.present || 0} />
							<Statistic title="지각" value={summary.late || 0} />
							<Statistic title="결석" value={summary.absent || 0} />
							<Statistic title="휴일" value={summary.holiday || 0} />
							<Statistic
								title="연차"
								value={summary.annual_leave || 0}
							/>
							<Statistic
								title="생리휴가"
								value={summary.menstrual_leave || 0}
							/>
							<Statistic
								title="병가"
								value={summary.sick_leave || 0}
							/>
							<Statistic
								title="출장"
								value={summary.business_trip || 0}
							/>
							<Statistic title="휴직" value={summary.on_leave || 0} />
							<Statistic title="사유서" value={summary.excuse || 0} />
						</Space>
						<div className="attendance-user-calendar attendance-user-calendar--weeks">
							<div className="attendance-user-calendar__week attendance-user-calendar__week--header">
								{WEEKDAYS.map((label) => (
									<div
										className={`attendance-user-calendar__weekday${
											label === "일"
												? " attendance-user-calendar__weekday--sunday"
												: ""
										}${
											label === "토"
												? " attendance-user-calendar__weekday--saturday"
												: ""
										}`}
										key={label}>
										{label}
									</div>
								))}
							</div>
							{calendarWeeks.map((week) => (
								<div
									className="attendance-user-calendar__week"
									key={week[0].format("YYYY-MM-DD")}>
									{week.map((day) => {
										const key = day.format("YYYY-MM-DD");
										const inMonth = day.isSame(month, "month");
										const isSunday = day.day() === 0;
										const isSaturday = day.day() === 6;
										const item = dateByKey[key];
										const cell = inMonth
											? employee.cells?.[key]
											: null;
										const today = day.isSame(dayjs(), "day");
										return (
											<div
												className={[
													"attendance-user-calendar__item",
													!inMonth
														? "attendance-user-calendar__item--outside"
														: "",
													inMonth && isSunday
														? "attendance-user-calendar__item--sunday"
														: "",
													today
														? "attendance-user-calendar__item--today"
														: "",
												]
													.filter(Boolean)
													.join(" ")}
												key={key}>
												<Text
													className={`attendance-user-calendar__date${
														isSunday
															? " attendance-user-calendar__date--sunday"
															: ""
													}${
														isSaturday
															? " attendance-user-calendar__date--saturday"
															: ""
													}`}>
													{day.date()}
												</Text>
												{inMonth && !isSunday ? (
													<>
														<AttendanceCell cell={cell} />
														<Text className="attendance-user-calendar__tip">
															{cell?.tooltip ||
																item?.work_type_name ||
																""}
														</Text>
													</>
												) : inMonth ? (
													<Text className="attendance-user-calendar__tip">
														주일
													</Text>
												) : null}
											</div>
										);
									})}
								</div>
							))}
						</div>
					</>
				) : (
					<Text type="secondary">조회할 교직원을 선택하세요.</Text>
				)}
			</Card>
			</div>
		</ConfigProvider>
	);
};

export default UserAttendance;
